import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Trash2, RefreshCcw, Eye, ShieldCheck, Crown, UserPlus, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";

function PlanBadge({ plan, source }) {
  const map = {
    free: "bg-muted text-muted-foreground",
    pro: "bg-[#002FA7]/15 text-[#002FA7]",
    unlimited: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase ${map[plan] || map.free}`}>
      {plan}
      {source === "trial" && <span className="text-[8px] opacity-70">·trial</span>}
      {source === "admin" && <span className="text-[8px] opacity-70">·comp</span>}
    </span>
  );
}

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function AdminUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(null); // user object
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (plan !== "all") params.plan = plan;
    if (includeDeleted) params.include_deleted = true;
    const { data } = await api.get("/admin/users", { params });
    setUsers(data.items);
    setTotal(data.total);
  }, [q, plan, includeDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (uid) => {
    const { data } = await api.get(`/admin/users/${uid}`);
    setDetail(data);
    setDetailOpen(true);
  };

  const grant = async (plan_id, days) => {
    if (!detail) return;
    try {
      await api.post(`/admin/users/${detail.user.id}/grant-plan`, { plan: plan_id, days });
      toast.success(`Granted ${plan_id} ${days ? `(${days}d)` : ""}`);
      const { data } = await api.get(`/admin/users/${detail.user.id}`);
      setDetail(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetUsage = async () => {
    if (!detail) return;
    await api.post(`/admin/users/${detail.user.id}/reset-usage`);
    toast.success(t("admin.users.resetUsage"));
    const { data } = await api.get(`/admin/users/${detail.user.id}`);
    setDetail(data);
    load();
  };

  const toggleField = async (field, value) => {
    if (!detail) return;
    await api.patch(`/admin/users/${detail.user.id}`, { [field]: value });
    const { data } = await api.get(`/admin/users/${detail.user.id}`);
    setDetail(data);
    load();
  };

  const saveNotes = async (notes) => {
    if (!detail) return;
    await api.patch(`/admin/users/${detail.user.id}`, { notes });
    toast.success(t("admin.users.savedNotes"));
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/admin/users/${delConfirm.id}`);
      toast.success("Deleted");
      if (detail?.user?.id === delConfirm.id) setDetailOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setDelConfirm(null);
    }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success("Compte réactivé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doSuspend = async (reason) => {
    if (!suspendOpen) return;
    try {
      await api.post(`/admin/users/${suspendOpen.id}/suspend`, { reason });
      toast.success("Compte suspendu");
      setSuspendOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const createUser = async (payload) => {
    try {
      await api.post("/admin/users", payload);
      toast.success("Utilisateur créé");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("admin.nav.users")}</div>
          <h1 className="mt-2 font-display text-2xl font-black tracking-tight">{t("admin.nav.users")}</h1>
          <p className="font-mono text-xs text-muted-foreground">{total} total</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="admin-create-user-btn">
          <UserPlus className="h-4 w-4" /> Créer un utilisateur
        </Button>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.users.search")}
            className="rounded-sm h-10 pl-9"
            data-testid="admin-users-search"
          />
        </div>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="rounded-sm h-10 w-40" data-testid="admin-users-filter-plan">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">{t("admin.users.all")}</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="unlimited">Unlimited</SelectItem>
          </SelectContent>
        </Select>
        <Label className="flex items-center gap-2 font-mono text-xs">
          <Switch checked={includeDeleted} onCheckedChange={setIncludeDeleted} data-testid="admin-users-include-deleted" />
          Afficher supprimés
        </Label>
      </div>

      {/* Table */}
      <div className="mt-6 border border-border bg-card">
        <div className="grid grid-cols-12 border-b border-border px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-5">user</div>
          <div className="col-span-2">{t("admin.users.plan")}</div>
          <div className="col-span-2">{t("admin.users.usage")}</div>
          <div className="col-span-2 text-center">jobs · accts</div>
          <div className="col-span-1 text-right">{t("admin.users.actions")}</div>
        </div>
        {users === null && <div className="p-6 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>}
        {users && users.length === 0 && <div className="p-6 font-mono text-sm text-muted-foreground">{t("admin.users.empty")}</div>}
        {users?.map((u) => (
          <div key={u.id} className="grid grid-cols-12 items-center border-b border-border px-4 py-3 last:border-0 text-sm">
            <div className="col-span-5 flex items-center gap-3 min-w-0">
              {u.picture ? (
                <img src={u.picture} alt="" className="h-8 w-8 rounded-sm" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center bg-[#002FA7] text-white font-mono text-xs">
                  {u.email[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="truncate font-display font-bold">{u.name || u.email}</div>
                <div className="truncate font-mono text-[11px] text-muted-foreground">{u.email}</div>
              </div>
              <div className="flex gap-1">
                {u.is_admin && <Badge className="rounded-sm bg-foreground text-background font-mono text-[10px]">admin</Badge>}
                {!u.is_active && <Badge variant="outline" className="rounded-sm font-mono text-[10px]">inactive</Badge>}
              </div>
            </div>
            <div className="col-span-2"><PlanBadge plan={u.effective_plan} source={u.plan_source} /></div>
            <div className="col-span-2 font-mono text-xs">{formatBytes(u.monthly_bytes_used || 0)}</div>
            <div className="col-span-2 text-center font-mono text-xs">{u.jobs_count} · {u.accounts_count}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <button
                onClick={() => (u.is_active ? setSuspendOpen(u) : reactivate(u))}
                className="rounded-sm border border-border p-1.5 hover:bg-accent"
                data-testid={`admin-user-suspend-${u.id}`}
                title={u.is_active ? "Suspendre" : "Réactiver"}
              >
                {u.is_active ? <PauseCircle className="h-3.5 w-3.5 text-amber-600" /> : <PlayCircle className="h-3.5 w-3.5 text-emerald-600" />}
              </button>
              <button
                onClick={() => openDetail(u.id)}
                className="rounded-sm border border-border p-1.5 hover:bg-accent"
                data-testid={`admin-user-view-${u.id}`}
                title={t("admin.users.view")}
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {detail?.user.name || detail?.user.email}
              {detail?.user.is_admin && (
                <Badge className="rounded-sm bg-foreground text-background font-mono text-[10px]">
                  <ShieldCheck className="mr-1 h-3 w-3" /> admin
                </Badge>
              )}
            </DialogTitle>
            <div className="font-mono text-xs text-muted-foreground">{detail?.user.email}</div>
          </DialogHeader>

          {detail && (
            <div className="space-y-6">
              {/* Profile toggles */}
              <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ToggleRow
                  label={t("admin.users.toggleActive")}
                  value={detail.user.is_active}
                  onChange={(v) => toggleField("is_active", v)}
                  testid="toggle-active"
                />
                <ToggleRow
                  label={t("admin.users.toggleAdmin")}
                  value={detail.user.is_admin}
                  onChange={(v) => toggleField("is_admin", v)}
                  testid="toggle-admin"
                />
              </section>

              {/* Billing */}
              <section className="border border-border p-4">
                <div className="font-mono text-[11px] uppercase text-muted-foreground">billing</div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <PlanBadge plan={detail.billing.plan} source={detail.billing.plan_source} />
                  <div className="font-mono text-xs">
                    {detail.billing.unlimited
                      ? "unlimited"
                      : `${formatBytes(detail.billing.used_bytes)} / ${formatBytes(detail.billing.quota_bytes)}`}
                  </div>
                  {detail.billing.plan_expires_at && (
                    <div className="font-mono text-[11px] text-muted-foreground">
                      exp: {new Date(detail.billing.plan_expires_at).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-2">
                  <GrantForm onGrant={grant} />
                  <Button
                    onClick={resetUsage}
                    variant="outline"
                    className="rounded-sm gap-2 h-9"
                    data-testid="admin-reset-usage"
                  >
                    <RefreshCcw className="h-3.5 w-3.5" /> {t("admin.users.resetUsage")}
                  </Button>
                </div>
              </section>

              {/* Stats */}
              <section className="grid grid-cols-3 gap-3">
                <MiniStat label="jobs" value={detail.jobs.length} />
                <MiniStat label="accounts" value={detail.accounts.length} />
                <MiniStat label="runs" value={detail.runs.length} />
              </section>

              {/* Recent runs (compact) */}
              {detail.runs.length > 0 && (
                <section>
                  <div className="font-mono text-[11px] uppercase text-muted-foreground">recent runs</div>
                  <div className="mt-2 border border-border max-h-48 overflow-y-auto">
                    {detail.runs.slice(0, 10).map((r) => (
                      <div key={r.id} className="flex items-center justify-between border-b border-border px-3 py-2 last:border-0 text-xs font-mono">
                        <span>{r.status} · {r.files_copied}↑ · {formatBytes(r.bytes_transferred)}</span>
                        <span className="text-muted-foreground">{new Date(r.started_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Notes */}
              <NotesEditor
                initial={detail.user.notes || ""}
                onSave={saveNotes}
                label={t("admin.users.notes")}
                saveLabel={t("admin.users.saveNotes")}
              />

              {/* Danger */}
              <section className="border border-destructive/30 p-4">
                <div className="font-mono text-[11px] uppercase text-destructive">danger zone</div>
                <Button
                  onClick={() => setDelConfirm(detail.user)}
                  variant="outline"
                  className="mt-2 rounded-sm gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                  data-testid="admin-delete-user"
                >
                  <Trash2 className="h-4 w-4" /> {t("admin.users.deleteUser")}
                </Button>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!delConfirm} onOpenChange={(o) => !o && setDelConfirm(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("admin.users.deleteUser")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.users.confirmDelete")}
              <div className="mt-2 font-mono text-xs">{delConfirm?.email}</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-user">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={deleteUser}
              data-testid="confirm-delete-user"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Créer un utilisateur</DialogTitle>
          </DialogHeader>
          <CreateUserForm onCreate={createUser} />
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={!!suspendOpen} onOpenChange={(o) => !o && setSuspendOpen(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Suspendre {suspendOpen?.email}</DialogTitle>
          </DialogHeader>
          <SuspendForm onConfirm={doSuspend} onCancel={() => setSuspendOpen(null)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateUserForm({ onCreate }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("free");
  const [isAdmin, setIsAdmin] = useState(false);
  const [grantTrial, setGrantTrial] = useState(true);
  const [notes, setNotes] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!email) return toast.error("Email requis");
    onCreate({ email, name, plan, is_admin: isAdmin, grant_trial: grantTrial, notes });
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="font-mono text-[11px] uppercase text-muted-foreground">Email</Label>
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="create-user-email" />
      </div>
      <div>
        <Label className="font-mono text-[11px] uppercase text-muted-foreground">Nom</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="create-user-name" />
      </div>
      <div>
        <Label className="font-mono text-[11px] uppercase text-muted-foreground">Plan</Label>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="mt-1 rounded-sm" data-testid="create-user-plan"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="unlimited">Unlimited</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3">
        <Label className="flex items-center gap-2 font-mono text-xs"><Switch checked={isAdmin} onCheckedChange={setIsAdmin} data-testid="create-user-admin" /> Admin</Label>
        <Label className="flex items-center gap-2 font-mono text-xs"><Switch checked={grantTrial} onCheckedChange={setGrantTrial} data-testid="create-user-trial" /> Essai 14j</Label>
      </div>
      <div>
        <Label className="font-mono text-[11px] uppercase text-muted-foreground">Notes</Label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-sm border border-border bg-background p-2 font-mono text-xs h-16" />
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">L'utilisateur devra se connecter via Google ou Microsoft avec cette adresse pour activer son compte.</p>
      <div className="flex justify-end">
        <Button type="submit" className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="create-user-submit">
          <UserPlus className="h-4 w-4" /> Créer
        </Button>
      </div>
    </form>
  );
}

function SuspendForm({ onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">L'utilisateur ne pourra plus se connecter. Les jobs planifiés restent en base mais ne tourneront plus.</p>
      <div>
        <Label className="font-mono text-[11px] uppercase text-muted-foreground">Motif (interne)</Label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 rounded-sm" data-testid="suspend-reason" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm" data-testid="suspend-cancel">Annuler</Button>
        <Button onClick={() => onConfirm(reason)} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="suspend-confirm">
          <PauseCircle className="h-4 w-4" /> Suspendre
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-border p-3">
      <span className="font-mono text-xs uppercase text-muted-foreground">{label}</span>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-border p-3">
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-black">{value}</div>
    </div>
  );
}

function GrantForm({ onGrant }) {
  const { t } = useI18n();
  const [plan, setPlan] = useState("pro");
  const [days, setDays] = useState("30");
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <Label className="font-mono text-[10px] uppercase text-muted-foreground">{t("admin.users.grantPlan")}</Label>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="mt-1 rounded-sm h-9 w-32" data-testid="admin-grant-plan-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="free">Free (revert)</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="unlimited">Unlimited</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="font-mono text-[10px] uppercase text-muted-foreground">{t("admin.users.grantDays")}</Label>
        <Input
          type="number"
          min={1}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="mt-1 rounded-sm h-9 w-24"
          data-testid="admin-grant-days"
        />
      </div>
      <Button
        onClick={() => onGrant(plan, plan === "free" ? null : Number(days))}
        className="h-9 rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-1"
        data-testid="admin-grant-confirm"
      >
        <Crown className="h-3.5 w-3.5" /> {t("admin.users.grantBtn")}
      </Button>
    </div>
  );
}

function NotesEditor({ initial, onSave, label, saveLabel }) {
  const [v, setV] = useState(initial);
  useEffect(() => setV(initial), [initial]);
  return (
    <section>
      <Label className="font-mono text-[11px] uppercase text-muted-foreground">{label}</Label>
      <textarea
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="mt-2 w-full rounded-sm border border-border bg-background p-3 font-mono text-sm h-24"
        data-testid="admin-notes-textarea"
      />
      <Button
        onClick={() => onSave(v)}
        className="mt-2 rounded-sm bg-foreground text-background hover:bg-foreground/90 h-9"
        data-testid="admin-notes-save"
      >
        {saveLabel}
      </Button>
    </section>
  );
}
